import { apolloClient } from '../api/client.js';
import { REPORT_ANALYTICS } from '../../graphql/mutations/index.js';
import { FEATURES } from '../../config/features.js';
import { useAuthStore } from '../stores/authStore.js';

const ANON_ID_KEY = 'yocas_analytics_anon_id';

function getOrCreateAnonId() {
  try {
    const existing = localStorage.getItem(ANON_ID_KEY);
    if (existing) return existing;
    const id = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `anon_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(ANON_ID_KEY, id);
    return id;
  } catch {
    return `anon_${Math.random().toString(36).slice(2)}_${Date.now()}`;
  }
}

function stableHash(input) {
  // Non-cryptographic stable hash for analytics identity only.
  const str = String(input ?? '');
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return `h_${(hash >>> 0).toString(16)}`;
}

/** @type {Array<{ category: string, action: string, identifier: string, userId: string, time: string, metadata?: string | null }>} */
let queue = [];
let timer = null;

/**
 * @param {{
 *   category: string,
 *   action: string,
 *   identifier?: string,
 *   userId?: string,
 *   metadata?: Record<string, any> | null,
 *   phone?: string,
 * }} event
 */
export function trackEvent({ category, action, identifier = '', userId = '', metadata = null, phone = '' }) {
  if (!FEATURES.analytics) return;

  const currentUserId = useAuthStore.getState().currentUser?.id || '';
  const finalUserId = userId || currentUserId || '';
  const anonId = getOrCreateAnonId();

  const finalMetadata =
    metadata || phone
      ? JSON.stringify({
          anonId,
          ...(phone ? { phoneHash: stableHash(phone) } : null),
          ...(metadata || null),
        })
      : JSON.stringify({ anonId });

  queue.push({
    category,
    action,
    identifier,
    userId: finalUserId,
    time: new Date().toISOString(),
    metadata: finalMetadata,
  });

  if (!timer) {
    timer = setTimeout(flushAnalytics, 15000);
  }
}

async function flushAnalytics() {
  timer = null;
  if (queue.length === 0) return;

  const items = [...queue];
  queue = [];

  try {
    await apolloClient.mutate({
      mutation: REPORT_ANALYTICS,
      variables: { items },
    });
  } catch {
    // silent fail per spec
  }
}

export function resetAnalyticsQueue() {
  queue = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
